/**
 * Email Validation API Utility
 * 
 * Uses external API for comprehensive email validation with fallback to client-side validation.
 * Supports multiple providers: AbstractAPI, ZeroBounce, EmailListVerify
 */

import { validateEmail as clientSideValidate } from './emailValidation';

// API Configuration
const EMAIL_VALIDATION_API = {
  // AbstractAPI - 100 free requests/month, then $0.001 per request
  abstractapi: {
    url: 'https://emailvalidation.abstractapi.com/v1/',
    apiKey: process.env.REACT_APP_ABSTRACTAPI_KEY || '',
    enabled: false
  },
  // ZeroBounce - 100 free credits/month
  zerobounce: {
    url: 'https://api.zerobounce.net/v2/validate',
    apiKey: process.env.REACT_APP_ZEROBOUNCE_API_KEY || '',
    enabled: true
  },
  // EmailListVerify - 100 free verifications/month
  emaillistverify: {
    url: 'https://apps.emaillistverify.com/api/verifEmail',
    apiKey: process.env.REACT_APP_EMAILLISTVERIFY_API_KEY || '',
    enabled: false
  }
};

// Profanity/inappropriate words filter
const INAPPROPRIATE_WORDS = [
  'piss', 'shit', 'fuck', 'damn', 'hell', 'ass', 'bitch', 'bastard',
  'crap', 'dick', 'cock', 'pussy', 'whore', 'slut', 'fag', 'nazi',
  'kike', 'spic', 'chink', 'gook', 'retard', 'idiot', 'stupid',
  'dumb', 'moron', 'imbecile', 'cunt', 'twat', 'wanker', 'tosser',
  'pissmouth', 'shithead', 'fuckface', 'asshole', 'dickhead', 'cocksucker'
];

/**
 * Check for inappropriate content in email
 * @param {string} email - Email address to check
 * @returns {Object} - { hasInappropriate: boolean, word?: string }
 */
const checkInappropriateContent = (email) => {
  const lowerEmail = email.toLowerCase();

  for (const word of INAPPROPRIATE_WORDS) {
    // Check if word appears in email (domain or local part)
    if (lowerEmail.includes(word)) {
      return {
        hasInappropriate: true,
        word: word
      };
    }
  }

  return { hasInappropriate: false };
};

/**
 * Validate email using AbstractAPI
 * @param {string} email - Email address to validate
 * @returns {Promise<Object>} - Validation result
 */
const validateWithAbstractAPI = async (email) => {
  const config = EMAIL_VALIDATION_API.abstractapi;

  if (!config.enabled || !config.apiKey) {
    return null; // API not configured
  }

  try {
    const response = await fetch(
      `${config.url}?api_key=${config.apiKey}&email=${encodeURIComponent(email)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();

    // AbstractAPI response structure
    const isValid = data.deliverability === 'DELIVERABLE' ||
      data.deliverability === 'UNDELIVERABLE' ? false :
      data.is_valid_format?.value === true;

    const isDisposable = data.is_disposable_email?.value === true;
    const isRoleEmail = data.is_role_email?.value === true;
    const isFreeEmail = data.is_free_email?.value === true;
    const qualityScore = data.quality_score || 0;

    return {
      valid: isValid && !isDisposable,
      error: !isValid ? 'Invalid email format' :
        isDisposable ? 'Disposable email addresses are not allowed' : null,
      details: [],
      isDisposable,
      isRoleEmail,
      isFreeEmail,
      qualityScore,
      source: 'abstractapi'
    };
  } catch (error) {
    console.error('AbstractAPI validation error:', error);
    return null; // Return null to trigger fallback
  }
};

/**
 * Validate email using ZeroBounce API
 * @param {string} email - Email address to validate
 * @returns {Promise<Object>} - Validation result
 */
const validateWithZeroBounce = async (email) => {
  const config = EMAIL_VALIDATION_API.zerobounce;

  if (!config.enabled || !config.apiKey) {
    return null;
  }

  try {
    const response = await fetch(
      `${config.url}?api_key=${config.apiKey}&email=${encodeURIComponent(email)}&ip_address=`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ZeroBounce API error:', response.status, errorText);
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();

    // ZeroBounce response structure
    const status = data.status?.toLowerCase();
    const isValid = status === 'valid';
    const isDisposable = status === 'do_not_mail' ||
      data.sub_status?.toLowerCase() === 'disposable' ||
      data.sub_status?.toLowerCase() === 'toxic' ||
      data.sub_status?.toLowerCase() === 'role_based_catchall';

    // Additional checks from ZeroBounce response
    const isRoleEmail = data.role === 'true' || data.role === true;
    const isCatchAll = data.catchall === 'true' || data.catchall === true;
    const isFreeEmail = data.free_email === 'true' || data.free_email === true;

    // Determine if email should be rejected
    let shouldReject = false;
    let rejectionReason = null;

    if (!isValid) {
      shouldReject = true;
      rejectionReason = data.sub_status || 'Invalid email address';
    } else if (isDisposable) {
      shouldReject = true;
      rejectionReason = 'Disposable email addresses are not allowed';
    } else if (isRoleEmail && !isFreeEmail) {
      // Allow role emails from free providers (like info@gmail.com)
      // But reject role emails from corporate domains
      shouldReject = true;
      rejectionReason = 'Role-based email addresses are not allowed';
    }

    return {
      valid: isValid && !shouldReject,
      error: shouldReject ? rejectionReason : null,
      details: [],
      isDisposable,
      isRoleEmail,
      isCatchAll,
      isFreeEmail,
      qualityScore: data.zerobounce?.credits_remaining || null,
      source: 'zerobounce',
      zerobounceData: {
        status: data.status,
        sub_status: data.sub_status,
        account: data.account,
        domain: data.domain,
        did_you_mean: data.did_you_mean
      }
    };
  } catch (error) {
    console.error('ZeroBounce validation error:', error);
    return null; // Return null to trigger fallback
  }
};

/**
 * Comprehensive email validation with API and fallback
 * @param {string} email - Email address to validate
 * @param {Object} options - Validation options
 * @param {boolean} options.useAPI - Whether to use API (default: true)
 * @param {boolean} options.checkProfanity - Whether to check for profanity (default: true)
 * @returns {Promise<Object>} - Validation result
 */
export const validateEmailWithAPI = async (email, options = {}) => {
  const { useAPI = true, checkProfanity = true } = options;

  if (!email || typeof email !== 'string') {
    return {
      valid: false,
      error: 'Email is required',
      details: [],
      source: 'client'
    };
  }

  const trimmedEmail = email.trim().toLowerCase();

  // First, check for inappropriate content (always check this)
  if (checkProfanity) {
    const profanityCheck = checkInappropriateContent(trimmedEmail);
    if (profanityCheck.hasInappropriate) {
      return {
        valid: false,
        error: 'Email contains inappropriate content',
        details: ['Please use a professional email address'],
        source: 'client',
        hasInappropriate: true
      };
    }
  }

  // Try API validation if enabled
  if (useAPI) {
    // Try ZeroBounce first (primary API)
    if (EMAIL_VALIDATION_API.zerobounce.enabled && EMAIL_VALIDATION_API.zerobounce.apiKey) {
      const apiResult = await validateWithZeroBounce(trimmedEmail);
      if (apiResult) {
        return apiResult;
      }
    }

    // Try AbstractAPI as fallback
    if (EMAIL_VALIDATION_API.abstractapi.enabled && EMAIL_VALIDATION_API.abstractapi.apiKey) {
      const apiResult = await validateWithAbstractAPI(trimmedEmail);
      if (apiResult) {
        return apiResult;
      }
    }
  }

  // Fallback to client-side validation
  const clientResult = clientSideValidate(trimmedEmail);
  return {
    ...clientResult,
    source: 'client'
  };
};

/**
 * Real-time email validation for input fields (uses API if available)
 * @param {string} email - Email address to validate
 * @param {Object} options - Validation options
 * @returns {Promise<Object>} - Validation state for UI
 */
export const getEmailValidationStateWithAPI = async (email, options = {}) => {
  if (!email || email.trim().length === 0) {
    return {
      isValid: null,
      message: '',
      showWarning: false
    };
  }

  const validation = await validateEmailWithAPI(email, options);

  if (validation.valid) {
    return {
      isValid: true,
      message: validation.suggestion || '',
      showWarning: !!validation.suggestion
    };
  }

  return {
    isValid: false,
    message: validation.error,
    showWarning: false
  };
};

/**
 * Check if API is configured and available
 * @returns {boolean} - True if at least one API is configured
 */
export const isAPIConfigured = () => {
  return (
    (EMAIL_VALIDATION_API.abstractapi.enabled && !!EMAIL_VALIDATION_API.abstractapi.apiKey) ||
    (EMAIL_VALIDATION_API.zerobounce.enabled && !!EMAIL_VALIDATION_API.zerobounce.apiKey) ||
    (EMAIL_VALIDATION_API.emaillistverify.enabled && !!EMAIL_VALIDATION_API.emaillistverify.apiKey)
  );
};

/**
 * Get API usage statistics (if available)
 * @returns {Object} - API stats
 */
export const getAPIStats = () => {
  return {
    abstractapi: {
      enabled: EMAIL_VALIDATION_API.abstractapi.enabled,
      configured: !!EMAIL_VALIDATION_API.abstractapi.apiKey
    },
    zerobounce: {
      enabled: EMAIL_VALIDATION_API.zerobounce.enabled,
      configured: !!EMAIL_VALIDATION_API.zerobounce.apiKey
    },
    emaillistverify: {
      enabled: EMAIL_VALIDATION_API.emaillistverify.enabled,
      configured: !!EMAIL_VALIDATION_API.emaillistverify.apiKey
    }
  };
};

